/**
 * Opens when `[data-js="quick-add"]` is clicked. Reads product data
 * from the originating card DOM + embedded JSON (server path) or
 * `__productData` (JS-hydrated path). Colour swatch taps inside the
 * drawer fetch `/products/{handle}?view=json` to refresh contents.
 */
(function () {
  if (window.__quickAddDrawerInit) return;
  window.__quickAddDrawerInit = true;

  const drawer = document.querySelector(
    '#quick-add-drawer[data-js="bottom-drawer"]',
  );
  if (!drawer) return;

  const sheet = drawer.querySelector('[data-js="bottom-drawer-sheet"]');
  const titleEl = drawer.querySelector('[data-js="quick-add-title"]');
  const colourEl = drawer.querySelector('[data-js="quick-add-colour"]');
  const pricesEl = drawer.querySelector('[data-js="quick-add-prices"]');
  const reviewsEl = drawer.querySelector('[data-js="quick-add-reviews"]');
  const swatchesEl = drawer.querySelector('[data-js="quick-add-swatches"]');
  const sizesEl = drawer.querySelector('[data-js="quick-add-sizes"]');
  const bannersEl = drawer.querySelector('[data-js="quick-add-banners"]');
  const addBtn = drawer.querySelector('[data-js="quick-add-submit"]');
  const addBtnLabel =
    addBtn && addBtn.querySelector('[data-js="quick-add-submit-label"]');
  const addBtnSpinner =
    addBtn && addBtn.querySelector('[data-js="quick-add-submit-spinner"]');
  const viewLink = drawer.querySelector('[data-js="quick-add-view-link"]');
  const wishBtn = drawer.querySelector('[data-js="quick-add-wishlist"]');
  const bodyEl = drawer.querySelector('[data-js="quick-add-body"]');
  const ratingTpl = document.querySelector('[data-js="quick-add-rating-template"]');

  const notifyPanel = drawer.querySelector('[data-js="notify-me"]');
  const notifyProductEl =
    notifyPanel && notifyPanel.querySelector('[data-js="notify-me-product"]');
  const notifyVariantInput =
    notifyPanel &&
    notifyPanel.querySelector('[data-js="notify-me-variant-id"]');
  const notifyEmail =
    notifyPanel && notifyPanel.querySelector('[data-js="notify-me-email"]');

  let autoCloseTimer = null;
  let activeCard = null;
  let selectedId = null;
  let selectedAvailable = true;
  let currentHandle = null;
  let currentTitle = "";
  let currentColour = "";
  let currentSize = "";

  /**
   * Read variant/size data from a product card.
   * JS-hydrated cards have `__productData`; server-rendered cards
   * embed a `<script data-js="quick-add-data">` JSON block.
   * @param {HTMLElement} card
   * @returns {{ size_label: string, variants: Array }|null}
   */
  function getVariantData(card) {
    if (card.__productData) {
      return {
        size_label: card.__productData.size_label || "",
        variants: card.__productData.variants || [],
      };
    }
    const script = card.querySelector('[data-js="quick-add-data"]');
    if (script) {
      try {
        return JSON.parse(script.textContent);
      } catch (_e) {
        /* fall through */
      }
    }
    return null;
  }

  /**
   * Build a muted looping gallery video for a media entry, mirroring the
   * card treatment: poster shown until playback frames arrive.
   * @param {{ src: string, videoSrc: string, alt: string }} entry
   * @param {boolean} eager - When true set poster/src now; otherwise defer
   *   both to `media-slider.js` hydration via data attributes.
   * @returns {HTMLVideoElement}
   */
  function createGalleryVideo(entry, eager) {
    const videoEl = document.createElement("video");
    // Muted must be set as a property before playback is requested —
    // several engines ignore the attribute alone on scripted elements
    // and then refuse to autoplay.
    videoEl.muted = true;
    videoEl.setAttribute("muted", "");
    videoEl.loop = true;
    videoEl.setAttribute("loop", "");
    videoEl.autoplay = true;
    videoEl.setAttribute("autoplay", "");
    videoEl.playsInline = true;
    videoEl.setAttribute("playsinline", "");
    videoEl.width = 3;
    videoEl.height = 4;
    videoEl.draggable = false;
    if (entry.alt) videoEl.setAttribute("aria-label", entry.alt);
    if (eager) {
      if (entry.src) videoEl.poster = entry.src;
      videoEl.src = entry.videoSrc;
    } else {
      if (entry.src) videoEl.dataset.poster = entry.src;
      videoEl.dataset.src = entry.videoSrc;
    }
    return videoEl;
  }

  /**
   * Read display data from the card DOM.
   * @param {HTMLElement} card
   * @returns {object}
   */
  function readCardDOM(card) {
    // Collect images sorted by original index — media-slider's
    // _recenter() shuffles DOM order, so naive querySelectorAll
    // would return them in the wrong sequence.
    const rawImgs = [];
    card
      .querySelectorAll('[data-js="product-card-slide"]')
      .forEach(function (slide) {
        // A slide holds an img, a video, or both — a poster img with the
        // overlay video `assets/product-card-video.js` layered onto it.
        const imgEl = slide.querySelector("img");
        const videoEl = slide.querySelector("video");
        if (!imgEl && !videoEl) return;
        let src = "";
        if (imgEl) {
          src =
            imgEl.getAttribute("src") ||
            imgEl.getAttribute("data-src") ||
            "";
        }
        if (!src && videoEl) {
          src =
            videoEl.getAttribute("poster") ||
            videoEl.getAttribute("data-poster") ||
            "";
        }
        if (!src) return;
        const videoSrc = videoEl
          ? videoEl.getAttribute("src") ||
            videoEl.getAttribute("data-src") ||
            ""
          : "";
        const idx = parseInt(
          slide.getAttribute("data-fresh-original-index"),
          10,
        );
        rawImgs.push({
          src: src,
          videoSrc: videoSrc,
          alt: (imgEl || videoEl).getAttribute("alt") || "",
          idx: Number.isFinite(idx) ? idx : 9999,
        });
      });
    rawImgs.sort(function (a, b) {
      return a.idx - b.idx;
    });
    const imgs = rawImgs;

    const title =
      (card.querySelector('[data-js="product-card-title"]') || {})
        .textContent || "";
    const colour =
      (card.querySelector('[data-js="product-card-colour-name"]') || {})
        .textContent || "";

    const priceEls = card.querySelectorAll('[data-js="product-card-price"]');
    const prices = [];
    priceEls.forEach(function (el) {
      prices.push({
        text: el.textContent.trim(),
        type: el.getAttribute("data-type") || "",
      });
    });

    const promoEl = card.querySelector('[data-js="product-card-promo"]');
    const promoText = promoEl ? promoEl.textContent.trim() : "";

    const swatches = card.querySelector('[data-js="colour-swatches"]');
    const bannerEls = card.querySelectorAll('[data-js="product-card-banner"]');
    const banners = [];
    bannerEls.forEach(function (el) {
      banners.push({
        text: el.textContent.trim(),
        style: el.getAttribute("style") || "",
      });
    });

    const link = card.querySelector('[data-js="product-card-details"]');
    const url = link ? link.getAttribute("href") : "";
    const handle = card.getAttribute("data-product-handle") || "";
    const productId = card.getAttribute("data-product-id") || "";
    let price = parseInt(card.getAttribute("data-price"), 10) || 0;
    const compareAtPriceAttr = card.getAttribute("data-compare-at-price");
    let compareAtPrice = parseInt(compareAtPriceAttr, 10) || 0;
    let promoPrice = price;
    if (card.__productData && card.__productData.promo_price) {
      promoPrice = parseInt(card.__productData.promo_price, 10) || price;
    } else if (promoText) {
      promoPrice = parseInt(card.getAttribute("data-promo-price"), 10) || price;
    }

    return {
      imgs,
      title: title.trim(),
      colour: colour.trim(),
      prices,
      promoText,
      promoPrice,
      swatches,
      banners,
      url,
      handle,
      productId,
      price,
      compareAtPrice,
      ratingNode: card.querySelector('[data-js="yotpo-rating"]'),
      yotpoBottomline: (card.__productData && card.__productData.yotpo_bottomline_raw) || null,
    };
  }

  /**
   * Render the size buttons, preselecting the only variant of a
   * single-size product.
   * @param {object|null} variantData - from getVariantData
   */
  function renderSizes(variantData) {
    sizesEl.textContent = "";
    if (
      variantData &&
      variantData.variants &&
      variantData.variants.length > 0
    ) {
      variantData.variants.forEach(function (v) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "quick-add-drawer__size";
        btn.dataset.js = "quick-add-size";
        btn.dataset.variantId = String(v.id);
        const avail = v.available === true || v.available_str === "true";
        btn.dataset.available = String(avail);
        btn.setAttribute("aria-pressed", "false");
        if (!avail) {
          btn.setAttribute("aria-disabled", "true");
          const span = document.createElement("span");
          span.className = "visually-hidden";
          span.textContent = `${drawer.dataset.labelSoldOut || "sold out"} `;
          btn.appendChild(span);
          btn.dataset.size = v.size;
          btn.appendChild(document.createTextNode(v.size));
        } else {
          btn.dataset.size = v.size;
          btn.textContent = v.size;
        }
        sizesEl.appendChild(btn);
      });
    }

    const singleVariant =
      variantData && variantData.variants && variantData.variants.length === 1;
    sizesEl.dataset.single = singleVariant ? "true" : "false";
    if (singleVariant) {
      const only = variantData.variants[0];
      const onlyBtn = sizesEl.querySelector('[data-js="quick-add-size"]');
      if (onlyBtn) {
        onlyBtn.dataset.selected = "true";
        onlyBtn.setAttribute("aria-pressed", "true");
      }
      selectedId = String(only.id);
      selectedAvailable =
        only.available === true || only.available_str === "true";
      currentSize = only.size || "";
    }
  }

  /**
   * Fill the drawer with data from a card + variant info.
   * @param {object} dom  - from readCardDOM
   * @param {object|null} variantData - from getVariantData
   */
  function populate(dom, variantData) {
    currentHandle = dom.handle;
    currentTitle = dom.title;
    currentColour = dom.colour;
    currentSize = "";
    selectedId = null;
    selectedAvailable = true;
    dynamicCheckoutPending = false;
    const cd = drawer.querySelector('[data-js="quick-add-dynamic-checkout"]');
    if (cd) delete cd.dataset.loading;
    closeNotifyPanel(true);

    const instalmentsEl = drawer.querySelector(
      '[data-js="quick-add-instalments"]',
    );
    if (instalmentsEl) {
      if (dom.price && dom.price > 0) {
        const moneyFmt =
          drawer.dataset.moneyFormat || window.themeSettings?.moneyFormat || "";
        let amount = "";
        const instalmentsPrice = dom.promoPrice || dom.price;
        if (window.AthosAPI && window.AthosAPI.formatMoney) {
          amount = window.AthosAPI.formatMoney(
            instalmentsPrice / 4,
            moneyFmt,
            true,
          );
        } else {
          const curr = window.Shopify?.currency?.active;
          if (curr) {
            amount = (instalmentsPrice / 4 / 100).toLocaleString(undefined, {
              style: "currency",
              currency: curr,
            });
          } else {
            amount = (instalmentsPrice / 4 / 100).toFixed(2);
          }
        }
        const tpl = instalmentsEl.getAttribute("data-template") || "";
        instalmentsEl.textContent = tpl.replace("[amount]", amount);
      } else {
        instalmentsEl.textContent = "";
      }
    }

    // Images — rebuild the entire media-slider so the CE re-initialises
    let mediaContainer = drawer.querySelector(".quick-add-drawer__media-wrap");
    if (!mediaContainer) {
      // First run: wrap the existing media-slider for easy replacement
      const existingSlider = drawer.querySelector("media-slider");
      if (existingSlider) {
        mediaContainer = document.createElement("div");
        mediaContainer.className = "quick-add-drawer__media-wrap";
        existingSlider.parentNode.insertBefore(mediaContainer, existingSlider);
        mediaContainer.appendChild(existingSlider);
      }
    }

    if (mediaContainer) {
      const newSlider = document.createElement("media-slider");
      newSlider.className = "quick-add-drawer__media";
      newSlider.setAttribute("data-swipeable", "");
      newSlider.setAttribute("aria-label", dom.title);

      const newTrack = document.createElement("div");
      newTrack.className = "media-slider__track";
      newTrack.dataset.js = "media-slider-track";

      const eagerSlideCount = 3;
      const reducedMotion = Boolean(
        window.themeUtils && window.themeUtils.prefersReducedMotion,
      );
      dom.imgs.forEach(function (img, index) {
        const slide = document.createElement("div");
        slide.className = "media-slider__slide";
        // Same shimmer treatment as card slides: skeleton.js flips
        // data-loaded on load/error, revealing the image or (via the
        // data-error rule in the drawer stylesheet) a quiet placeholder
        // instead of the browser's broken-image glyph. Video slides reveal
        // the same way — skeleton.js also listens for `loadeddata`.
        slide.dataset.skeleton = "";
        slide.dataset.skeletonScale = "false";
        let mediaEl;
        if (img.videoSrc && !reducedMotion) {
          mediaEl = createGalleryVideo(img, index < eagerSlideCount);
        } else {
          mediaEl = document.createElement("img");
          if (index < eagerSlideCount) {
            mediaEl.src = img.src;
          } else {
            mediaEl.dataset.src = img.src;
          }
          mediaEl.alt = img.alt;
          mediaEl.width = 3;
          mediaEl.height = 4;
          mediaEl.draggable = false;
        }
        mediaEl.addEventListener(
          "error",
          function () {
            slide.dataset.error = "true";
          },
          { once: true },
        );
        slide.appendChild(mediaEl);
        newTrack.appendChild(slide);
      });

      newSlider.appendChild(newTrack);

      if (dom.imgs.length > 1) {
        newSlider.setAttribute("data-progress-bar", "true");
      }

      const desktopGrid = document.createElement("div");
      desktopGrid.className = "quick-add-drawer__desktop-media";

      dom.imgs.forEach(function (img) {
        const item = document.createElement("div");
        item.className = "quick-add-drawer__desktop-media-item";
        let mediaEl;
        if (img.videoSrc && !reducedMotion) {
          mediaEl = createGalleryVideo(img, true);
        } else {
          mediaEl = document.createElement("img");
          mediaEl.loading = "lazy";
          mediaEl.src = img.src;
          mediaEl.alt = img.alt;
          mediaEl.width = 3;
          mediaEl.height = 4;
          mediaEl.draggable = false;
        }
        item.appendChild(mediaEl);
        desktopGrid.appendChild(item);
      });

      mediaContainer.textContent = "";
      mediaContainer.appendChild(newSlider);
      mediaContainer.appendChild(desktopGrid);

      // Images from cache fire no load event, so sweep them into their
      // revealed state, then load the active slide first and every other
      // slide behind it — the 2.2-slide peek keeps neighbours on screen,
      // so anything less leaves visible slides sitting on the shimmer.
      if (window.themeSkeleton && typeof window.themeSkeleton.sweep === "function") {
        window.themeSkeleton.sweep(newSlider);
      }
      if (typeof newSlider.loadActive === "function") {
        newSlider.loadActive();
      }
      if (typeof newSlider.loadAll === "function") {
        newSlider.loadAll();
      }
    }

    titleEl.textContent = dom.title;
    colourEl.textContent = dom.colour;

    pricesEl.textContent = "";
    dom.prices.forEach(function (p) {
      const span = document.createElement("span");
      span.className = "quick-add-drawer__price";
      if (p.type) span.setAttribute("data-type", p.type);
      span.textContent = p.text;
      pricesEl.appendChild(span);
    });
    if (dom.promoText) {
      const promoSpan = document.createElement("span");
      promoSpan.className = "quick-add-drawer__price";
      promoSpan.setAttribute("data-type", "promo");
      promoSpan.setAttribute("data-js", "quick-add-promo");
      promoSpan.textContent = dom.promoText;
      pricesEl.appendChild(promoSpan);
    }

    // Colour swatches — clone from card, but rename the data-js
    // attribute so product-card-swap.js ignores clicks inside the
    // drawer (prevents a second fetch to the same endpoint).
    swatchesEl.textContent = "";
    if (dom.swatches) {
      const clone = dom.swatches.cloneNode(true);
      clone
        .querySelectorAll('[data-js="product-card-colour"]')
        .forEach(function (el) {
          el.dataset.js = "quick-add-colour";
        });
      swatchesEl.appendChild(clone);
    }

    bannersEl.textContent = "";
    dom.banners.forEach(function (b) {
      const div = document.createElement("div");
      div.className = "quick-add-drawer__banner";
      if (b.style) div.setAttribute("style", b.style);
      div.textContent = b.text;
      bannersEl.appendChild(div);
    });

    if (reviewsEl) {
      reviewsEl.textContent = "";
      if (dom.ratingNode && dom.ratingNode.dataset.initialised === "true") {
        // The source card's scaffold already carries its settled rating
        // state (same markup family) — clone it instead of re-resolving.
        reviewsEl.appendChild(dom.ratingNode.cloneNode(true));
      } else if (dom.productId && ratingTpl && ratingTpl.content.firstElementChild) {
        const rating = ratingTpl.content.firstElementChild.cloneNode(true);
        rating.dataset.productId = dom.productId;
        reviewsEl.appendChild(rating);
        if (window.YotpoRatings && typeof window.YotpoRatings.prefill === "function") {
          window.YotpoRatings.prefill(rating, dom.yotpoBottomline || null);
        }
      }
    }

    renderSizes(variantData);

    if (wishBtn) {
      wishBtn.dataset.productId = dom.productId;
      wishBtn.__productData = {
        id: dom.productId,
        handle: dom.handle,
        title: dom.title,
        price: dom.price,
        compareAtPrice: dom.compareAtPrice || 0,
        promoPrice: dom.promoPrice || 0,
        promoText: dom.promoText || "",
        image: dom.imgs.length > 0 ? dom.imgs[0].src : "",
        variants: variantData ? variantData.variants : [],
      };
    }

    viewLink.href = dom.url || `/products/${dom.handle}`;

    updateAddBtn();
    updateSwatchesLimit();
  }

  /**
   * Adjust the visible swatches limit based on viewport width.
   * Desktop allows up to 11 swatches before overflow; mobile allows 5.
   */
  function updateSwatchesLimit() {
    if (!swatchesEl) return;
    const container = swatchesEl.querySelector('[data-js="colour-swatches"]');
    if (!container) return;
    if (container.dataset.expanded === "true") return;

    const swatches = Array.from(
      container.querySelectorAll('[data-js="quick-add-colour"]'),
    );
    let btn = container.querySelector('[data-js="colour-overflow-toggle"]');
    if (swatches.length <= 5) return;

    const limit = window.innerWidth >= 768 ? 11 : 5;
    const overflowCount = swatches.length - limit;

    swatches.forEach(function (swatch, idx) {
      if (idx >= limit) {
        swatch.dataset.overflow = "true";
      } else {
        swatch.removeAttribute("data-overflow");
      }
    });

    if (overflowCount > 0) {
      if (!btn) {
        btn = document.createElement("button");
        btn.type = "button";
        btn.className = "product-card__colour-overflow";
        btn.dataset.js = "colour-overflow-toggle";
      }
      btn.textContent = `+${overflowCount}`;
      btn.style.display = "";

      const firstOverflow = container.querySelector('[data-overflow="true"]');
      if (firstOverflow) {
        container.insertBefore(btn, firstOverflow);
      } else {
        container.appendChild(btn);
      }
    } else {
      if (btn) btn.style.display = "none";
    }
  }

  let swatchesResizeTimer;
  window.addEventListener("resize", function () {
    if (drawer.dataset.state !== "open") return;
    clearTimeout(swatchesResizeTimer);
    swatchesResizeTimer = setTimeout(updateSwatchesLimit, 100);
  });

  /**
   * Update the add-to-bag button label and state. Always restores the
   * label/spinner span structure so transient states (loading, added,
   * error) don't leak between renders.
   *
   * Three resting states:
   *   - no size picked      → disabled, "Select Size"
   *   - in-stock size       → enabled, "Add to bag"
   *   - sold-out size       → enabled (when Klaviyo BIS is configured),
   *                           "Notify me" — clicking opens the notify panel
   *
   * @param {string} [override] - Optional explicit label to show.
   */
  function updateAddBtn(override) {
    delete addBtn.dataset.state;
    if (addBtnSpinner) addBtnSpinner.hidden = true;
    if (addBtnLabel) addBtnLabel.hidden = false;
    if (typeof override === "string") {
      addBtn.disabled = true;
      setAddBtnLabel(override);
      updateDynamicCheckoutState();
      return;
    }
    if (!selectedId) {
      addBtn.disabled = true;
      setAddBtnLabel(addBtn.dataset.labelSelect);
      updateDynamicCheckoutState();
      return;
    }
    if (!selectedAvailable && notifyPanel) {
      addBtn.disabled = false;
      addBtn.dataset.mode = "notify";
      setAddBtnLabel(addBtn.dataset.labelNotify || "Notify me");
      updateDynamicCheckoutState();
      return;
    }
    if (!selectedAvailable) {
      // BIS disabled — fall back to a disabled state rather than a broken click.
      addBtn.disabled = true;
      delete addBtn.dataset.mode;
      setAddBtnLabel(
        addBtn.dataset.labelUnavailable || addBtn.dataset.labelSelect,
      );
      updateDynamicCheckoutState();
      return;
    }
    delete addBtn.dataset.mode;
    addBtn.disabled = false;
    setAddBtnLabel(addBtn.dataset.labelAdd);
    updateDynamicCheckoutState();
  }

  /**
   * Mirror the add-to-bag button's enabled state on the Shop Pay
   * accelerated checkout container. The accelerated checkout is
   * rendered by Shopify as a web component, so we can't disable it
   * directly — we toggle a data attribute that CSS uses to block
   * pointer events, and the capture-phase click handler enforces
   * the same rule programmatically.
   */
  function updateDynamicCheckoutState() {
    const checkoutDiv = drawer.querySelector(
      '[data-js="quick-add-dynamic-checkout"]',
    );
    if (!checkoutDiv) return;
    const enabled = Boolean(selectedId) && selectedAvailable;
    checkoutDiv.dataset.disabled = enabled ? "false" : "true";
    checkoutDiv.setAttribute("aria-disabled", enabled ? "false" : "true");
  }

  /**
   * Set the button label, falling back to direct textContent if the
   * inner label span isn't present (defensive — markup should always
   * provide it via the snippet).
   * @param {string} text
   */
  function setAddBtnLabel(text) {
    if (addBtnLabel) {
      addBtnLabel.textContent = text || "";
    } else {
      addBtn.textContent = text || "";
    }
  }

  let sheetPreviousHeight = null;

  /**
   * Measure the natural content height of the notify panel.
   *
   * @returns {number}
   */
  function measureNotifyHeight() {
    if (!notifyPanel) return 0;

    // To measure the panel's natural height, we temporarily remove its
    // absolute/inset constraints, and remove the flex scaling from its body
    // so it expands to fit its content naturally.
    const prevPosition = notifyPanel.style.position;
    notifyPanel.style.position = "relative";

    const panelBodyEl = notifyPanel.querySelector(".notify-me__body");
    let prevFlex = "";
    if (panelBodyEl) {
      prevFlex = panelBodyEl.style.flex;
      panelBodyEl.style.flex = "none";
    }

    const total = notifyPanel.offsetHeight;

    // Restore original styles
    if (panelBodyEl) {
      panelBodyEl.style.flex = prevFlex;
    }
    notifyPanel.style.position = prevPosition;

    return total;
  }

  /**
   * Reset the notify-me form to its initial state, keeping the shopper's
   * contact details for the next size. Called when the panel is opened,
   * when the drawer's product changes, and when the panel is closed after
   * a successful submission.
   */
  function resetNotifyForm() {
    if (!notifyPanel) return;
    if (klaviyoNotifyMe) klaviyoNotifyMe.reset({ keepContact: true });
  }

  /**
   * The current product's other sold-out sizes, offered as one-tap extra
   * notify-me subscriptions after a successful submission.
   * @returns {Array<{id: string, label: string}>}
   */
  function otherSoldOutSizes() {
    if (!sizesEl) return [];
    return Array.from(sizesEl.querySelectorAll('[data-js="quick-add-size"][data-available="false"]'))
      .filter(function (btn) {
        return btn.dataset.variantId !== String(selectedId);
      })
      .map(function (btn) {
        return { id: btn.dataset.variantId, label: btn.dataset.size || btn.textContent.trim() };
      });
  }

  /**
   * Open the notify-me panel and smoothly resize the sheet to fit its
   * content. The sheet's current height is captured as an explicit
   * pixel value first so the CSS `height` transition has a defined
   * starting frame to interpolate from.
   */
  function openNotifyPanel() {
    if (!notifyPanel || !selectedId || selectedAvailable) return;
    resetNotifyForm();
    if (klaviyoNotifyMe) klaviyoNotifyMe.setOtherSizes(otherSoldOutSizes());
    if (notifyVariantInput) notifyVariantInput.value = String(selectedId);
    if (notifyProductEl) {
      const parts = [currentTitle];
      if (currentColour) parts.push(currentColour);
      if (currentSize) parts.push(currentSize);
      notifyProductEl.textContent = parts.filter(Boolean).join(" · ");
    }

    notifyPanel.hidden = false;
    notifyPanel.setAttribute("aria-hidden", "false");
    drawer.dataset.notify = "true";

    if (sheet) {
      if (window.innerWidth < 768) {
        const startHeight = sheet.getBoundingClientRect().height;
        sheetPreviousHeight = startHeight;
        sheet.style.height = `${startHeight}px`;
        // Force a reflow so the start height is observed before mode flip.
        void sheet.offsetHeight;
      }
      sheet.dataset.mode = "notify";
    }

    void notifyPanel.offsetWidth;
    notifyPanel.dataset.state = "open";

    if (sheet && window.innerWidth < 768) {
      requestAnimationFrame(function () {
        const target = measureNotifyHeight();
        if (target > 0) sheet.style.height = `${target}px`;
      });
    }

    requestAnimationFrame(function () {
      if (notifyEmail) notifyEmail.focus();
    });
  }

  /**
   * @param {boolean} [immediate] - When true, skip the slide-out transition
   *   (used when populate() resets state during a colour swap).
   */
  function closeNotifyPanel(immediate) {
    if (!notifyPanel) return;
    if (notifyPanel.dataset.state !== "open" && !immediate) return;

    notifyPanel.dataset.state = immediate ? "closed" : "closing";
    notifyPanel.setAttribute("aria-hidden", "true");
    delete drawer.dataset.notify;

    if (sheet) {
      delete sheet.dataset.mode;
      if (immediate || window.innerWidth >= 768) {
        sheet.style.height = "";
      } else {
        // Pin the current pixel height then transition back to the
        // height the sheet had before notify opened.
        const current = sheet.getBoundingClientRect().height;
        sheet.style.height = `${current}px`;
        const target = sheetPreviousHeight;
        requestAnimationFrame(function () {
          if (target != null) {
            sheet.style.height = `${target}px`;
          } else {
            sheet.style.height = "";
          }
        });
      }
      sheetPreviousHeight = null;
    }

    if (immediate) {
      notifyPanel.hidden = true;
      notifyPanel.dataset.state = "closed";
      resetNotifyForm();
      return;
    }

    const onEnd = function (e) {
      // Only react to the panel's own transform transition, ignoring
      // the sheet height transition that's running concurrently.
      if (e && e.target !== notifyPanel) return;
      notifyPanel.removeEventListener("transitionend", onEnd);
      if (notifyPanel.dataset.state === "closing") {
        notifyPanel.dataset.state = "closed";
        notifyPanel.hidden = true;
        resetNotifyForm();
        if (sheet) sheet.style.height = "";
      }
    };
    notifyPanel.addEventListener("transitionend", onEnd);
    setTimeout(function () {
      onEnd();
    }, 600);
  }

  let klaviyoNotifyMe = null;
  if (notifyPanel && typeof window.KlaviyoNotifyMe !== "undefined") {
    try {
      klaviyoNotifyMe = new window.KlaviyoNotifyMe(notifyPanel, {
        onResize: resizeNotifySheet,
        onClose: closeNotifyPanel,
      });
    } catch (err) {
      klaviyoNotifyMe = null;
    }
  }

  /**
   * Re-measure the notify panel and animate the sheet height to fit.
   * Used when content changes (e.g. an error message appears or clears)
   * so the drawer grows/shrinks instead of clipping or leaving a gap.
   */
  function resizeNotifySheet() {
    if (!sheet || !notifyPanel) return;
    if (notifyPanel.dataset.state !== "open") return;
    if (window.innerWidth >= 768) {
      sheet.style.height = "";
      return;
    }
    requestAnimationFrame(function () {
      const target = measureNotifyHeight();
      if (target > 0) sheet.style.height = `${target}px`;
    });
  }

  /** @param {HTMLElement} card */
  function open(card) {
    if (drawer.dataset.state === "open" || drawer.dataset.state === "closing")
      return;
    clearTimeout(autoCloseTimer);
    activeCard = card;
    const dom = readCardDOM(card);
    const variantData = getVariantData(card);

    try {
      populate(dom, variantData);
    } catch (_err) {
      return;
    }

    if (!variantData || !variantData.variants || variantData.variants.length === 0) {
      hydrateMissingSizes(card, dom.handle);
    }

    updateShopPayVisibility();
    updateDynamicCheckoutState();

    if (window.BottomDrawer) {
      window.BottomDrawer.open("quick-add-drawer");
    }
  }

  /**
   * Fetch a product's card data (media, prices, swatches, sizes) from the
   * `product.json` view.
   * @param {string} handle
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>}
   */
  async function fetchProductData(handle, signal) {
    if (window.ProductCardSwap && window.ProductCardSwap.fetchCardData) {
      return window.ProductCardSwap.fetchCardData(handle);
    }
    const res = await fetch(`${window.Shopify.routes.root}products/${handle}?view=json`, {
      signal,
    });
    if (!res.ok) throw new Error("quick-add: product fetch failed");
    return JSON.parse(await res.text());
  }

  /**
   * Athos results don't always carry sizes, which leaves Athos-rendered
   * cards with no variants. Fetch them from the product instead, and keep
   * them on the card so reopening it is instant.
   * @param {HTMLElement} card
   * @param {string} handle
   */
  async function hydrateMissingSizes(card, handle) {
    let data;
    try {
      data = await fetchProductData(handle);
    } catch (_err) {
      return;
    }
    const variantData = {
      size_label: data.size_label || "",
      variants: data.variants || [],
    };
    if (variantData.variants.length === 0) return;
    if (card.__productData) {
      card.__productData.size_label = variantData.size_label;
      card.__productData.variants = variantData.variants;
    }
    if (activeCard !== card || currentHandle !== handle) return;
    renderSizes(variantData);
    if (wishBtn && wishBtn.__productData) {
      wishBtn.__productData.variants = variantData.variants;
    }
    updateAddBtn();
  }

  function updateShopPayVisibility(addedQuantity = 0) {
    const countEl = document.querySelector(
      "[data-minicart-count], .js-cart-count",
    );
    const count =
      (countEl ? parseInt(countEl.textContent, 10) || 0 : 0) + addedQuantity;
    const checkoutDiv = drawer.querySelector(
      '[data-js="quick-add-dynamic-checkout"]',
    );
    if (checkoutDiv) {
      if (count > 0) {
        checkoutDiv.removeAttribute("hidden");
      } else {
        checkoutDiv.setAttribute("hidden", "");
      }
    }
  }

  function close() {
    if (window.BottomDrawer) {
      window.BottomDrawer.close("quick-add-drawer");
    }
  }

  drawer.addEventListener("drawer:close", function () {
    if (
      activeCard &&
      currentHandle &&
      activeCard.getAttribute("data-product-handle") !== currentHandle
    ) {
      document.dispatchEvent(
        new CustomEvent("product-card:request-swap", {
          detail: { card: activeCard, handle: currentHandle },
          bubbles: true,
        }),
      );
    }
  });

  drawer.addEventListener("drawer:closed", function () {
    clearTimeout(autoCloseTimer);
    activeCard = null;
  });

  drawer.addEventListener("drawer:open", function () {
    if (bodyEl) bodyEl.scrollTop = 0;
    const mediaWrap = drawer.querySelector(".quick-add-drawer__media-wrap");
    if (mediaWrap) mediaWrap.scrollTop = 0;
    const contentRight = drawer.querySelector(
      ".quick-add-drawer__content-right",
    );
    if (contentRight) contentRight.scrollTop = 0;
  });

  let colourSwapAbort = null;

  /**
   * Fetch product JSON and re-populate the drawer.
   * @param {string} handle
   */
  /**
   * Toggle the colour-swap loading state: `data-loading` on the dialog
   * (dims the swapping content) and on the pending swatch (pulses its
   * dot). Cleared wholesale so rapid re-clicks never strand a marker.
   * @param {string} handle - The handle being swapped to; ignored when off.
   * @param {boolean} isLoading
   */
  function setSwapLoading(handle, isLoading) {
    if (window.BottomDrawer && typeof window.BottomDrawer.setLoading === "function") {
      window.BottomDrawer.setLoading(drawer, isLoading);
    }
    if (!swatchesEl) return;
    swatchesEl
      .querySelectorAll('[data-js="quick-add-colour"]')
      .forEach(function (swatch) {
        if (isLoading && swatch.dataset.handle === handle) {
          swatch.dataset.loading = "true";
        } else {
          delete swatch.dataset.loading;
        }
      });
  }

  async function swapColour(handle) {
    if (colourSwapAbort) colourSwapAbort.abort();
    colourSwapAbort = new AbortController();
    const signal = colourSwapAbort.signal;

    setSwapLoading(handle, true);

    try {
      const oldSwatches = drawer.querySelector('[data-js="colour-swatches"]');
      const wasExpanded =
        oldSwatches && oldSwatches.dataset.expanded === "true";

      const data = await fetchProductData(handle, signal);

      if (signal.aborted) return;

      // Build DOM-like structures from fetched data
      const imgs = (data.media || []).map(function (m) {
        return {
          src: m.image,
          videoSrc: m.is_video === "true" ? m.video_url || "" : "",
          alt: m.alt || data.title || "",
        };
      });

      const prices = [];
      if (data.on_sale && data.formatted_compare_at_price) {
        prices.push({ text: data.formatted_compare_at_price, type: "sale" });
      }
      prices.push({
        text: data.formatted_price || "",
        type: data.price_type || "",
      });

      // Build swatch container from colour_options
      let swatchDiv = null;
      if (data.colour_options && data.colour_options.length > 0) {
        swatchDiv = document.createElement("div");
        swatchDiv.className = "product-card__colours";
        swatchDiv.dataset.js = "colour-swatches";
        if (oldSwatches && oldSwatches.dataset.i18nSoldOut) {
          swatchDiv.dataset.i18nSoldOut = oldSwatches.dataset.i18nSoldOut;
        }
        if (wasExpanded) {
          swatchDiv.dataset.expanded = "true";
        }
        data.colour_options.forEach(function (c) {
          const a = document.createElement("a");
          a.className = "product-card__colour";
          a.dataset.js = "quick-add-colour";
          a.dataset.handle = c.handle;
          a.href = c.url;
          a.setAttribute("style", c.style_str);
          a.setAttribute("aria-label", c.colour || c.name || c.handle);
          if (c.active_str === "true") a.dataset.active = "true";
          if (c.overflow_str === "true") a.dataset.overflow = "true";
          swatchDiv.appendChild(a);
        });

        if (data.colour_overflow && data.colour_overflow > 0) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "product-card__colour-overflow";
          btn.dataset.js = "colour-overflow-toggle";
          btn.textContent = `+${data.colour_overflow}`;
          const firstOverflow = swatchDiv.querySelector(
            '[data-overflow="true"]',
          );
          if (firstOverflow) {
            swatchDiv.insertBefore(btn, firstOverflow);
          } else {
            swatchDiv.appendChild(btn);
          }
        }
      }

      let banners = [];
      if (data.banners) {
        banners = data.banners.map(function (b) {
          return { text: b.text, style: b.style_str || "" };
        });
      }

      const dom = {
        imgs: imgs,
        title: data.title || "",
        colour: data.colour || "",
        prices: prices,
        swatches: swatchDiv,
        banners: banners,
        url: data.product_url || `/products/${handle}`,
        handle: handle,
        productId: String(data.id || ""),
        price: data.price || 0,
        compareAtPrice: data.compare_at_price || 0,
        promoText: data.promo_message || "",
        promoPrice: data.promo_price || data.price || 0,
        yotpoBottomline: data.yotpo_bottomline_raw || null,
      };

      const variantData = {
        size_label: data.size_label || "",
        variants: data.variants || [],
      };

      populate(dom, variantData);
    } catch (_err) {
      // Silent fail — drawer stays with current data
    } finally {
      // An aborted request means a newer swap owns the loading state.
      if (!signal.aborted) setSwapLoading(handle, false);
    }
  }

  async function performAddToCart() {
    const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: Number(selectedId), quantity: 1 }],
      }),
    });
    const body = await res.json().catch(function () {
      return {};
    });
    return { res, body };
  }

  async function addToCart() {
    if (!selectedId) return;

    addBtn.disabled = true;
    addBtn.dataset.state = "loading";
    if (addBtnLabel) addBtnLabel.hidden = true;
    if (addBtnSpinner) addBtnSpinner.hidden = false;

    try {
      const { res, body } = await performAddToCart();

      if (!res.ok) {
        addBtn.dataset.state = "error";
        if (addBtnSpinner) addBtnSpinner.hidden = true;
        if (addBtnLabel) addBtnLabel.hidden = false;
        setAddBtnLabel(body.description || addBtn.dataset.labelError || "");
        setTimeout(function () {
          updateAddBtn();
        }, 2000);
        return;
      }

      // Success: show "Added to bag" inline, then auto-close.
      // We intentionally pass `openMinicart: false` on the cart:added
      // detail so the minicart drawer refreshes the header bag count
      // without opening.
      addBtn.dataset.state = "added";
      if (addBtnSpinner) addBtnSpinner.hidden = true;
      if (addBtnLabel) addBtnLabel.hidden = false;
      setAddBtnLabel(addBtn.dataset.labelAdded || "Added to bag");

      document.dispatchEvent(
        new CustomEvent("cart:added", {
          detail: {
            sections: body.sections || null,
            openMinicart: false,
          },
        }),
      );

      if (activeCard) {
        const addedItem =
          body.items && body.items.length > 0 ? body.items[0] : null;
        activeCard.dispatchEvent(
          new CustomEvent("quick-add:added", {
            bubbles: true,
            detail: { item: addedItem },
          }),
        );
      }

      updateShopPayVisibility(1);
      // The added animation settles at ~850ms (sheen 700ms, tick 850ms);
      // this leaves roughly a second of read time before closing.
      clearTimeout(autoCloseTimer);
      autoCloseTimer = setTimeout(function () {
        close();
      }, 1800);
    } catch (_err) {
      addBtn.dataset.state = "error";
      if (addBtnSpinner) addBtnSpinner.hidden = true;
      if (addBtnLabel) addBtnLabel.hidden = false;
      setAddBtnLabel(addBtn.dataset.labelError || "Error");
      setTimeout(function () {
        updateAddBtn();
      }, 2000);
    }
  }

  // Open drawer
  document.addEventListener(
    "click",
    function (e) {
      const btn = e.target.closest('[data-js="quick-add"]');
      if (!btn) return;
      e.preventDefault();
      // Note: stopPropagation here may suppress Athos beacon card-click tracking,
      // but removing it may trigger card navigation if not handled elsewhere.
      e.stopPropagation();
      const card = btn.closest('[data-js="product-card"]');
      if (card) open(card);
    },
    true,
  );

  drawer.addEventListener("click", function (e) {
    clearTimeout(autoCloseTimer);
  });

  // Native dialog handling for Escape key while notify panel is open
  drawer.addEventListener("drawer:cancel", function (e) {
    if (notifyPanel && notifyPanel.dataset.state === "open") {
      e.preventDefault();
      closeNotifyPanel();
    }
  });

  // Size guide swap logic
  let swappedCard = null;
  drawer
    .querySelectorAll('[data-drawer-open="size-guide-drawer"]')
    .forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (activeCard) {
          swappedCard = activeCard;
        }
        close();

        const targetId = btn.getAttribute("data-drawer-open");
        if (targetId && window.BottomDrawer) {
          window.BottomDrawer.open(targetId);
        }
      });
    });

  window.themeUtils?.on?.("overlay-hide", function (e) {
    if (e.detail && e.detail.source === "size-guide-drawer") {
      if (swappedCard) {
        const cardToReopen = swappedCard;
        swappedCard = null;
        // Reopen quick add drawer after a slight delay for smooth swapping
        setTimeout(function () {
          open(cardToReopen);
        }, 50);
      }
    }
  });

  // Size selection
  sizesEl.addEventListener("click", function (e) {
    clearTimeout(autoCloseTimer);
    const btn = e.target.closest('[data-js="quick-add-size"]');
    if (!btn) return;

    sizesEl
      .querySelectorAll('[data-js="quick-add-size"]')
      .forEach(function (b) {
        b.dataset.selected = "false";
        b.setAttribute("aria-pressed", "false");
      });
    btn.dataset.selected = "true";
    btn.setAttribute("aria-pressed", "true");
    selectedId = btn.dataset.variantId;
    selectedAvailable = btn.dataset.available !== "false";
    currentSize = btn.dataset.size || btn.textContent.trim();
    updateAddBtn();
  });

  addBtn.addEventListener("click", function (e) {
    e.preventDefault();
    if (addBtn.dataset.mode === "notify") {
      openNotifyPanel();
      return;
    }
    addToCart();
  });

  // Dynamic (Shop Pay) accelerated checkout — gate by selected size, add
  // the variant to the cart, then navigate same-tab into Shop Pay
  // checkout of the live cart.
  let dynamicCheckoutPending = false;
  const dynamicCheckoutDiv = drawer.querySelector(
    '[data-js="quick-add-dynamic-checkout"]',
  );
  if (dynamicCheckoutDiv) {
    dynamicCheckoutDiv.addEventListener(
      "click",
      function (e) {
        if (!selectedId || !selectedAvailable || dynamicCheckoutPending) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        addThenAcceleratedCheckout();
      },
      true,
    );
  }

  /**
   * Intercepts a click on the Shop Pay express button, adds the
   * currently-selected variant to the cart, then navigates same-tab to
   * Shop Pay checkout of the live cart (which now includes the item).
   * The loading state is left in place on success — the navigation
   * unloads the page, so there is nothing to reset back to.
   */
  async function addThenAcceleratedCheckout() {
    dynamicCheckoutPending = true;
    if (dynamicCheckoutDiv) dynamicCheckoutDiv.dataset.loading = "true";
    // Lock the add-to-bag button so a second click can't race a
    // duplicate add request while we're mid-flight.
    addBtn.disabled = true;
    addBtn.dataset.state = "loading";
    if (addBtnLabel) addBtnLabel.hidden = true;
    if (addBtnSpinner) addBtnSpinner.hidden = false;
    try {
      const { res, body } = await performAddToCart();
      if (!res.ok) {
        resetDynamicCheckoutState();
        return;
      }
      document.dispatchEvent(
        new CustomEvent("cart:added", {
          detail: {
            sections: body.sections || null,
            openMinicart: false,
          },
        }),
      );

      if (activeCard) {
        const addedItem =
          body.items && body.items.length > 0 ? body.items[0] : null;
        activeCard.dispatchEvent(
          new CustomEvent("quick-add:added", {
            bubbles: true,
            detail: { item: addedItem },
          }),
        );
      }
      if (
        window.ShopPayCheckout &&
        typeof window.ShopPayCheckout.buildHref === "function"
      ) {
        window.location.assign(window.ShopPayCheckout.buildHref());
        return;
      }
      resetDynamicCheckoutState();
      close();
    } catch (_err) {
      resetDynamicCheckoutState();
    }
  }

  /** Restore the drawer's CTA state after a failed Shop Pay submit. */
  function resetDynamicCheckoutState() {
    dynamicCheckoutPending = false;
    if (dynamicCheckoutDiv) delete dynamicCheckoutDiv.dataset.loading;
    if (addBtnSpinner) addBtnSpinner.hidden = true;
    if (addBtnLabel) addBtnLabel.hidden = false;
    updateAddBtn();
  }

  // Swatch click inside drawer
  swatchesEl.addEventListener("click", function (e) {
    clearTimeout(autoCloseTimer);
    const swatch = e.target.closest('[data-js="quick-add-colour"]');
    if (!swatch) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = swatch.dataset.handle;
    if (!handle || handle === currentHandle) return;
    swapColour(handle);
  });

  if (notifyPanel) {
    notifyPanel
      .querySelectorAll('[data-js="notify-me-close"]')
      .forEach(function (btn) {
        btn.addEventListener("click", function () {
          closeNotifyPanel();
        });
      });
  }
})();
